import { useEffect, useState } from "react";
import { FileSignature, Lock } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const CONCLUSIONS = [
  { value: "A", title: "Alternativ A: Systemet fungerar tillfredsställande och inga ytterligare risker har identifierats.", desc: "Kan endast väljas om inga olösta generella brister finns i loggen." },
  { value: "B", title: "Alternativ B: Systemet fungerar men riskanalysen/åtgärderna ska uppdateras.", desc: "Löpande förbättring krävs baserat på identifierade generella brister under året." },
  { value: "C", title: "Alternativ C: Systemet har INTE fungerat tillfredsställande.", desc: "Kräver omedelbar uppdatering och omfattande handlingsplan." },
];

export default function Module4({ refreshDash }) {
  const { user } = useAuth();
  const isManager = ["Kvalitetsansvarig", "Byråledning", "Supervisor"].includes(user.role);
  const isSupervisor = user.role === "Supervisor";
  const year = new Date().getFullYear();
  const [data, setData] = useState(null);
  const [deviations, setDeviations] = useState([]);
  const [conclusion, setConclusion] = useState("B");
  const [notes, setNotes] = useState("");
  const [signing, setSigning] = useState(false);

  const load = () => {
    api.get(`/evaluations?year=${year}`).then((r) => setData(r.data)).catch(() => {});
    api.get("/deviations").then((r) => setDeviations(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const setStatus = async (dev, status) => {
    try {
      await api.put(`/deviations/${dev.id}`, { status, resolution_notes: dev.resolution_notes || "" });
      toast.success(`Avvikelse uppdaterad → ${status}`);
      load();
      refreshDash?.();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const sign = async () => {
    setSigning(true);
    try {
      await api.post("/evaluations", { fiscal_year: year, conclusion, summary_notes: notes });
      toast.success(`Årsrapporten för ${year} är signerad och låst – slutsats ${conclusion}`);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSigning(false);
    }
  };

  if (!data) return <div className="text-sm text-slate-500">Laddar...</div>;
  const blocked = data.conclusion_a_blocked;
  const u = data.underlag;

  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6" data-testid="modul4-view">
      <div className="border-b border-slate-200 pb-4 mb-6">
        <div className="text-xs font-bold text-blue-600 uppercase tracking-wider">Modul 4 · Reko 220–225</div>
        <h2 className="text-xl font-extrabold text-slate-900">Årlig Utvärdering av Kvalitetsstyrningssystemet</h2>
        <p className="text-sm text-slate-500 mt-1">Utvärdera systemets efterlevnad och effekt minst en gång om året (p. 1-2).</p>
      </div>

      <div className="space-y-6">
        <div className="bg-slate-50 p-5 rounded-lg border border-slate-200 space-y-3">
          <h3 className="font-bold text-slate-800 text-sm">Del 1: Sammanställt Underlag från Systemet</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-xs">
            <div className="bg-white p-3 rounded border border-slate-200">
              <div className="text-slate-500">Rapporterade Brister (Totalt):</div>
              <div className="text-lg font-bold text-slate-800" data-testid="underlag-total">{u.total} st</div>
            </div>
            <div className="bg-white p-3 rounded border border-slate-200">
              <div className="text-slate-500">Enstaka Händelser:</div>
              <div className="text-lg font-bold text-slate-800" data-testid="underlag-enstaka">{u.enstaka} st</div>
            </div>
            <div className="bg-white p-3 rounded border border-slate-200">
              <div className="text-slate-500">Generella / Systemiska Brister:</div>
              <div className="text-lg font-bold text-red-600" data-testid="underlag-generella">{u.generella} st ({u.olosta_generella} olösta)</div>
            </div>
          </div>
        </div>

        <div className="bg-slate-50 p-5 rounded-lg border border-slate-200 space-y-3">
          <h3 className="font-bold text-slate-800 text-sm">Inkomna Avvikelser & Kvalitetsbrister</h3>
          <div className="overflow-x-auto">
            <table className="w-full text-left text-xs border-collapse" data-testid="modul4-deviations-table">
              <thead>
                <tr className="bg-slate-100 text-slate-600 font-semibold border-b border-slate-200">
                  <th className="p-3">Datum</th><th className="p-3">Kund</th><th className="p-3">Rapporterad av</th>
                  <th className="p-3">Typ</th><th className="p-3">Beskrivning</th><th className="p-3">Status</th><th className="p-3 text-right">Åtgärd</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {deviations.map((d) => (
                  <tr key={d.id}>
                    <td className="p-3 font-mono text-slate-500">{d.created_at?.slice(0, 10)}</td>
                    <td className="p-3 font-bold text-slate-800">{d.client_name}</td>
                    <td className="p-3">{d.reported_by_name}</td>
                    <td className="p-3"><span className={`px-2 py-0.5 rounded font-bold ${d.type === "Generell/Systemisk" ? "bg-red-100 text-red-800" : "bg-amber-100 text-amber-800"}`}>{d.type}</span></td>
                    <td className="p-3 max-w-xs">{d.description}{d.requires_risk_update && <span className="block text-red-600 font-semibold mt-0.5">Kräver uppdatering av riskanalys</span>}</td>
                    <td className="p-3 font-bold">{d.status}</td>
                    <td className="p-3 text-right">
                      {isManager && d.status !== "GODKÄND" && d.status !== "ARKIVERAD" ? (
                        <div className="flex justify-end space-x-1.5">
                          {d.status === "OLÖST" && (
                            <button data-testid={`deviation-investigate-${d.id}`} onClick={() => setStatus(d, "UNDER_UTREDNING")} className="bg-slate-100 hover:bg-slate-200 text-slate-700 px-2.5 py-1 rounded font-semibold">Utred</button>
                          )}
                          <button data-testid={`deviation-approve-${d.id}`} onClick={() => setStatus(d, "GODKÄND")} className="bg-emerald-600 hover:bg-emerald-700 text-white px-2.5 py-1 rounded font-semibold">Godkänn</button>
                        </div>
                      ) : (
                        <span className="text-slate-400">—</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="bg-slate-50 p-5 rounded-lg border border-slate-200 space-y-4">
          <h3 className="font-bold text-slate-800 text-sm">Del 2: Fastställande av Slutsats för Kvalitetsstyrningssystemet (p. 2)</h3>
          <p className="text-xs text-slate-600">Kvalitetsansvarig måste välja en (1) av tre fastställda Reko-slutsatser:</p>
          <div className="space-y-3 text-xs">
            {CONCLUSIONS.map((c) => {
              const disabled = c.value === "A" && blocked;
              return (
                <label
                  key={c.value}
                  data-testid={`conclusion-${c.value.toLowerCase()}`}
                  className={`flex items-start p-3 bg-white border rounded ${disabled ? "border-slate-200 opacity-50 cursor-not-allowed" : "border-slate-200 cursor-pointer hover:bg-blue-50/50"}`}
                >
                  <input
                    type="radio" name="rekoConclusion" value={c.value} disabled={disabled || !isSupervisor || !!data.evaluation}
                    checked={conclusion === c.value} onChange={() => setConclusion(c.value)} className="mt-0.5 mr-3 text-blue-600"
                  />
                  <div>
                    <span className={`font-bold ${disabled ? "text-slate-700" : "text-slate-800"}`}>{c.title}</span>
                    {disabled ? (
                      <p className="text-red-600 font-semibold mt-0.5 flex items-center"><Lock className="w-3 h-3 mr-1" />Spärrad: Kan inte väljas eftersom det finns olösta generella brister i loggen.</p>
                    ) : (
                      <p className="text-slate-500 mt-0.5">{c.desc}</p>
                    )}
                  </div>
                </label>
              );
            })}
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-700 mb-1">Sammanfattande anteckningar (obligatoriskt):</label>
            <textarea
              data-testid="evaluation-notes-input"
              className="w-full text-xs p-3 border border-slate-300 rounded focus:ring-2 focus:ring-blue-500 h-20 disabled:opacity-60"
              value={notes} onChange={(e) => setNotes(e.target.value)}
              disabled={!isSupervisor || !!data.evaluation}
              placeholder="Sammanfatta årets utvärdering, orsaker till brister och planerade åtgärder..."
            />
          </div>
        </div>

        <div className="bg-slate-900 text-white p-6 rounded-xl space-y-4">
          <h3 className="font-bold text-base text-blue-300">Del 3: Digital Signering av Årsrapporten</h3>
          <p className="text-xs text-slate-300">Härmed intygas att utvärderingen av medlemsföretagets kvalitetsstyrningssystem har genomförts i enlighet med kraven i Reko. Åtgärder och slutsatser är dokumenterade med vem och när (p. 1-2).</p>
          {data.evaluation ? (
            <div className="p-4 bg-emerald-900/40 border border-emerald-700/50 rounded-lg text-xs" data-testid="evaluation-signed-banner">
              <div className="text-emerald-300 font-bold flex items-center"><Lock className="w-3.5 h-3.5 mr-1.5" />Årsrapport {data.evaluation.fiscal_year} signerad & låst — Slutsats {data.evaluation.conclusion}</div>
              <div className="text-slate-300 mt-1 font-mono">Signerad: {new Date(data.evaluation.signed_at).toLocaleString("sv-SE")}</div>
              <div className="text-slate-400 mt-1">{data.evaluation.summary_notes}</div>
            </div>
          ) : (
            <div className="flex justify-between items-center pt-2 flex-wrap gap-3">
              <div className="text-xs text-slate-400 font-mono">Utförare: {user.name} ({user.role}) · Datum stämplas vid signering · Endast Supervisor kan signera</div>
              {isSupervisor && (
                <button
                  data-testid="evaluation-sign-btn" onClick={sign} disabled={signing}
                  className="bg-blue-600 hover:bg-blue-500 disabled:opacity-60 text-white text-xs font-extrabold px-5 py-2.5 rounded shadow flex items-center"
                >
                  <FileSignature className="w-3.5 h-3.5 mr-1.5" />
                  {signing ? "SIGNERAR..." : `SIGNERA & LÅS ÅRSRAPPORT ${year}`}
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
